import { IDestroyOptions, Rectangle, Sprite } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ButtonWithScaleAndPosYAnimation } from "../../../common/components/ButtonWithScaleAndPosYAnimation";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { IFocusable } from "../../../layers/IFocusable";
import { LandlordInfoFrame } from "../components/LandlordInfoFrame";
import { RenterInfoFrame } from "../components/RenterInfoFrame";
import { LandlordRenterInfoResourcePackage } from "../resources/LandlordRenterInfoResourcePackage";

export class LandlordRenterInfoPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly landlordRenterInfoResources: LandlordRenterInfoResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private fadeScreen: FadeScreen;
    private starsBackground: Sprite;
    private topLight: Sprite;
    private lights: Sprite[];
    private landlordInfoFrame: LandlordInfoFrame;
    private renterInfoFrame: RenterInfoFrame;
    private leftStar: Sprite;
    private rightStar: Sprite;
    private closeButton: ButtonWithScaleAndPosYAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        landlordRenterInfoResources: LandlordRenterInfoResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler
    ) {
        super(operationControls, canvasScaler);
        this.landlordRenterInfoResources = landlordRenterInfoResources;
        this.commonResources = commonResources;
        this.lights = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.fadeScreen.fadeIn();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.topLight = new Sprite(this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_top_light_png);
        this.addChild(this.topLight);
        this.starsBackground = new Sprite(this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_stars_background_png);
        this.addChild(this.starsBackground);
        this.landlordInfoFrame = new LandlordInfoFrame(this.landlordRenterInfoResources);
        this.addChild(this.landlordInfoFrame);
        this.leftStar = new Sprite(this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_star_left_png);
        this.addChild(this.leftStar);
        this.rightStar = new Sprite(this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_star_right_png);
        this.addChild(this.rightStar);
        this.renterInfoFrame = new RenterInfoFrame(this.landlordRenterInfoResources);
        this.addChild(this.renterInfoFrame);
        this.closeButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);
        this.addChild(this.closeButton);
        this.createLights();
    }

    protected layout(width: number, height: number): void {
        this.landlordInfoFrame.position.set(26.06, 307.58);
        this.renterInfoFrame.position.set(26.06, 930.16);
        this.leftStar.position.set(151.5, 326.58);
        this.rightStar.position.set(606.05, 320.4);
        this.closeButton.position.set(width * 0.5 - this.closeButton.width * 0.5, 1630);
        this.lights[0].position.set(0, 63);
        this.lights[1].position.set(width, 63);
        this.lights[2].position.set(0, 625);
        this.lights[3].position.set(width, 625);
    }

    private createLights(): void {
        const lightSprites = [
            this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_yellow_light_png,
            this.landlordRenterInfoResources.assets_views_landlord_renter_info_images_green_light_png
        ];

        for (const sprite of lightSprites) {
            const light = new Sprite(sprite);
            const lightFlipped = new Sprite(sprite);
            lightFlipped.scale.x *= -1;
            this.lights.push(light, lightFlipped);
            this.addChild(light, lightFlipped);
        }
    }

    private async closeButtonClickHandle(): Promise<void> {
        this.operationControls.complete();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        super.destroy(options);
    }
}
